import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { driverSupabase as supabase } from '@/integrations/supabase/driver-client';
import { getDriverSessionId, handleDispatchResult } from '@/lib/driverSession';
import { ensureAdminAlertsChannel } from '@/lib/pushChannel';
import { getPrimedFcmToken } from '@/lib/fcmBootstrap';

// Module-level cache of the last FCM token observed via the
// `'registration'` listener. Used by signOut() to scope its
// push_tokens deactivation to this device's row only.
let cachedFcmToken: string | null = null;

/**
 * localStorage key recording the (driverId, token) pair that has been
 * successfully written to the backend `push_tokens` row. Verify OTP sets
 * this immediately after `driver-login.verify_otp` succeeds; the safety
 * net in `useFcmRegistration` uses it to skip duplicate writes when the
 * OS reports the same token Verify OTP already registered.
 */
const LAST_REGISTERED_KEY = 'driver_fcm_last_registered';

type LastRegistered = { driverId: string; token: string };

export function getLastRegisteredFcm(): LastRegistered | null {
  try {
    const raw = localStorage.getItem(LAST_REGISTERED_KEY);
    return raw ? (JSON.parse(raw) as LastRegistered) : null;
  } catch {
    return null;
  }
}

export function setLastRegisteredFcm(value: LastRegistered | null) {
  try {
    if (value) localStorage.setItem(LAST_REGISTERED_KEY, JSON.stringify(value));
    else localStorage.removeItem(LAST_REGISTERED_KEY);
  } catch { /* ignore */ }
}

function isAlreadyRegistered(driverId: string, token: string): boolean {
  const last = getLastRegisteredFcm();
  return !!last && last.driverId === driverId && last.token === token;
}

/**
 * Read the last FCM token observed on this device. Used by `signOut` to
 * scope its `push_tokens` deactivation to THIS device's row only — so a
 * background sign-out on a kicked-out device cannot deactivate the
 * brand-new row a different device just inserted.
 */
export function getCachedFcmToken(): string | null {
  return cachedFcmToken;
}

/**
 * Mint a brand-new FCM token synchronously for the caller (used by
 * `verifyOtp` BEFORE invoking `driver-login`, so the new token is
 * created server-side at Verify OTP as the single authoritative point).
 *
 * Returns null on non-Android / non-native / no-permission / failure.
 * Updates the module cache so other code paths can reuse the same token.
 */
export async function mintFreshFcmToken(
  timeoutMs = 18000,
): Promise<{ token: string; platform: string } | null> {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  if (platform !== 'android') return null;
  try {
    // IMPORTANT: token minting must NOT depend on POST_NOTIFICATIONS
    // permission. On Android, FCM can mint a registration token even when
    // the user has not granted notification display permission — the
    // permission only controls whether notifications are shown, not
    // whether a token exists. Gating token creation on permission caused
    // the "notification ON triggers token creation" bug across re-logins.
    const { FCM } = await import('@capacitor-community/fcm');
    // NOTE: we intentionally do NOT call PushNotifications.register() here.
    // That would emit a 'registration' event to the listener in
    // useFcmRegistration with a token that may differ from the one
    // FCM.refreshToken() returns to us synchronously below, causing a
    // duplicate token write outside the OTP path. FCM.refreshToken()
    // does not require register() to have been called first on Android.
    // ALWAYS rotate the FCM installation on attempt 0, regardless of any
    // local marker. Without this, a login on a device whose marker was
    // cleared (signOut, forceSignOutForOtherDevice, fresh storage wipe)
    // would call refreshToken() on the same Firebase Installation and
    // get back the SAME token that was just hard-deleted server-side —
    // breaking the "every login produces a brand-new token" invariant.
    // The bumped timeoutMs (18s) absorbs the delete + refresh round-trip
    // even on cold-start / brand-new installs.
    const refreshPromise = (async () => {
      // Retry up to 3 attempts with backoff on transient FCM failures
      // (Play Services warm-up, transient network, etc).
      let lastErr: unknown = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          if (attempt === 0) {
            try { await FCM.deleteInstance(); } catch { /* non-fatal */ }
          }
          const { token } = await FCM.refreshToken();
          if (token) return token;
        } catch (err) {
          lastErr = err;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (lastErr) console.warn('[fcm] refreshToken retries exhausted', lastErr);
      return null;
    })();
    const token = await Promise.race<string | null>([
      refreshPromise,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    if (!token) return null;
    cachedFcmToken = token;
    return { token, platform };
  } catch (err) {
    console.warn('[fcm] mintFreshFcmToken failed', err);
    return null;
  }
}

export async function postRegisterFcmToken(driverId: string, token: string) {
  const platform = Capacitor.getPlatform();
  try {
    const res = await supabase.functions.invoke('dispatch-trip', {
      body: {
        action: 'register_fcm_token',
        driver_id: driverId,
        fcm_token: token,
        platform,
        session_id: getDriverSessionId(),
      },
    });
    handleDispatchResult(res);
    if (res?.data?.success === false) {
      console.warn('[fcm] register_fcm_token rejected', res?.data?.error);
    } else {
      setLastRegisteredFcm({ driverId, token });
      console.log('[fcm] register_fcm_token ok');
    }
  } catch (err) {
    console.warn('[fcm] register_fcm_token failed', err);
  }
}

/**
 * Registers the device with Firebase Cloud Messaging on Android, and sends
 * the resulting FCM token to the backend so dispatch-trip can push
 * high-priority trip-offer notifications when the app is backgrounded /
 * killed / on the lock screen.
 *
 * - Android only (no-op on iOS / web).
 * - Requires `@capacitor/push-notifications`, a Firebase project, and a
 *   `google-services.json` placed in `android/app/`.
 * - POST_NOTIFICATIONS is requested by an explicit user tap on the Home
 *   screen's `NotificationPermissionSheet` ("Allow" button). This hook
 *   awaits `notificationPermissionReady` BEFORE its own
 *   `requestPermissions()` so FCM never silently consumes the OS dialog;
 *   `register()` is then called regardless of grant state. On iOS / web
 *   `notificationPermissionReady` resolves immediately so FCM init never
 *   blocks waiting for a non-existent prompt.
 */
export function useFcmRegistration(driverId: string | undefined) {
  useEffect(() => {
    if (!driverId) return;
    if (!Capacitor.isNativePlatform()) return;

    let cleanupListeners: Array<{ remove: () => Promise<void> }> = [];
    let cancelled = false;

    // Login-time token creation is owned EXCLUSIVELY by `driver-login`
    // (Verify OTP) — `verifyOtp` mints a fresh token client-side and the
    // edge function writes it atomically with the new session id. This
    // hook is a pure SAFETY NET for OS-driven token rotation that happens
    // OUTSIDE a login (e.g. FCM server-side rotation while the app is
    // running). It is diff-gated by `driver_fcm_last_registered`: the
    // backend `register_fcm_token` action is only invoked when the OS
    // reports a token that differs from the one Verify OTP already
    // registered. This is what stops duplicate push_tokens writes per
    // login. Token registration is independent of notification permission.

    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const { LocalNotifications } = await import('@capacitor/local-notifications');

        // Pre-create the high-importance `trip-offers` channel BEFORE we
        // call register(), so that any FCM message received on cold start
        // (the manifest sets `default_notification_channel_id=trip-offers`)
        // can be displayed by the OS even before any other JS has run.
        try {
          await LocalNotifications.createChannel({
            id: 'trip-offers-v2',
            name: 'Trip Offers',
            description: 'Incoming trip offers',
            importance: 5,
            visibility: 1,
            vibration: true,
            lights: true,
          });
        } catch (err) {
          console.warn('[fcm] createChannel failed', err);
        }

        // Channel the Admin Portal targets for operational pushes.
        // Without this, Admin-sent notifications are silently dropped on
        // Android 8+ because the channel referenced in the FCM payload
        // does not exist on the device.
        await ensureAdminAlertsChannel();

        // Do NOT call FCM.deleteInstance() here. Verify OTP already minted
        // a fresh token and persisted it to the DB. Deleting the instance
        // on every cold start invalidated that token and forced this hook
        // to re-register a different token — the root cause of duplicate
        // `push_tokens` writes per login. True OS-revoked tokens are
        // handled when `registration` fires with a new value AND it
        // differs from `getLastRegisteredFcm()`.

        const regListener = await PushNotifications.addListener('registration', async (token) => {
          if (cancelled) return;
          cachedFcmToken = token.value;
          // Diff-gate: if Verify OTP already registered this exact token
          // for this driver, do nothing. This covers the common case where
          // Android replays the registration callback after a permission
          // grant or app resume with the SAME token Verify OTP minted —
          // which is what made notification ON appear to create a token.
          if (isAlreadyRegistered(driverId, token.value)) {
            console.log('[fcm] token unchanged — skipping register_fcm_token');
            return;
          }
          // A different token can only mean a true OS-driven rotation
          // (rare). Require an active driver session before touching the
          // backend so a forced-out / signed-out device can never create
          // or overwrite a token row that belongs to the new device.
          const session = getDriverSessionId();
          if (!session) {
            console.log('[fcm] no active session — skipping rotation write');
            return;
          }
          console.log('[fcm] OS token rotated — posting to backend');
          await postRegisterFcmToken(driverId, token.value);
        });

        const errListener = await PushNotifications.addListener('registrationError', (err) => {
          // The most common cause is the Google Services Gradle plugin not
          // being applied (so google-services.json is ignored at build).
          // See scripts/patch-android-manifest.mjs and the README.
          console.error('[fcm] registrationError — Firebase failed to init. Check google-services.json + Google Services Gradle plugin.', err);
        });

        cleanupListeners.push(regListener, errListener);
        await PushNotifications.register();

        // Proactive post-login self-heal: if no `driver_fcm_last_registered`
        // marker exists for this driver (e.g. the inline mint in verifyOtp
        // returned null on a slow Play Services start, or the user just
        // logged in on a warm app where `register()` won't re-fire), mint
        // and POST a token row directly. Bounded retry; gives up silently.
        (async () => {
          for (let attempt = 0; attempt < 6 && !cancelled; attempt++) {
            const last = getLastRegisteredFcm();
            if (last && last.driverId === driverId) return;
            if (!getDriverSessionId()) {
              // Wait for verifyOtp to finish writing the session id.
              await new Promise((r) => setTimeout(r, 2000));
              continue;
            }
            // Prefer the token primed at app boot. Avoids repeatedly
            // deleting the Firebase Installation (which mintFreshFcmToken
            // does on attempt 0) and churning through Installation IDs.
            const primed = getPrimedFcmToken();
            let token: string | null = primed ?? null;
            if (!token) {
              const minted = await mintFreshFcmToken();
              token = minted?.token ?? null;
            }
            if (token) {
              await postRegisterFcmToken(driverId, token);
              const after = getLastRegisteredFcm();
              if (after && after.driverId === driverId) return;
            }
            await new Promise((r) => setTimeout(r, 3000 + attempt * 1500));
          }
        })();

        // NOTE: we intentionally do NOT re-call PushNotifications.register()
        // on app resume or when notification permission changes. Doing so
        // turned "notification permission ON" into a token-creation trigger
        // (Android fires the 'registration' callback after a permission
        // grant), which broke the "Verify OTP is the only token creator"
        // contract. Real OS-driven token rotations are rare and the next
        // cold start (or the next login) will re-establish the token via
        // the diff-gated path above.
      } catch (err) {
        console.warn('[fcm] init failed', err);
      }
    })();

    return () => {
      cancelled = true;
      cleanupListeners.forEach((l) => {
        try { l.remove(); } catch { /* ignore */ }
      });
    };
  }, [driverId]);
}